import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Incident, GeoLocation } from '../types';
import { Layers } from 'lucide-react';
import { eventsApi, CorroboratedEvent } from '../services/postsApi';
import { subscribeToActiveRound } from '../services/gameService';
import { mountGameScene, dismountGameScene, updateGameScene, flyToGameScene } from './GameScene3D';
import { mountLandmarks, dismountLandmarks } from './Landmarks3D';

mapboxgl.accessToken = 'pk.eyJ1Ijoibm9ibGVnbGl0Y2giLCJhIjoiY21uMzI4OG9yMWFlMTJ0b3RiM21jaXkycCJ9.WlazfNk0cPc0d-vYBX2g4w';

// ── Map style: Sonoran Midnight (Tucson Streets v3) ──────────────────────────
// Brand: deep midnight basemap + cyan-accent highways (matches noble3
// --accent:#00f5ff) + bronze/amber POI labels + Catalinas hillshade for terrain.
// Goal: the map IS the product — looks unmistakably "tactical Tucson" even at
// a glance, with mountains, water (CAP, Santa Cruz wash), and 3D downtown all
// visible at typical zoom levels.
// Layer naming convention: `road-highway`, `road-street`, etc. Custom content
// layers (events, garage_sales, businesses, helicopters) are added later via
// `addLayer()` calls so they live above the basemap and can be toggled in the
// layer-control UI without modifying the style spec.
const modernWesternStyle: any = {
  version: 8,
  name: 'Tucson Sonoran Midnight',
  glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
  sources: {
    'mapbox-streets': { type: 'vector', url: 'mapbox://mapbox.mapbox-streets-v8' },
    'mapbox-dem': {
      type: 'raster-dem',
      url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
      tileSize: 256,
      maxzoom: 11,
    },
  },
  // Subtle terrain — exaggerate just enough to feel the Catalinas/Rincons,
  // not so much that streets look like roller coasters.
  terrain: { source: 'mapbox-dem', exaggeration: 1.25 },
  // Warm desert sunset light angling in from the west — makes mountain faces
  // catch a bronze rim while the basin stays cool midnight blue.
  light: { anchor: 'viewport', color: '#ffedd5', intensity: 0.85, position: [1.5, 220, 45] },
  layers: [
    // Deep midnight base — matches the noble3 dark-cyber palette.
    { id: 'background', type: 'background', paint: { 'background-color': '#03070f' } },
    {
      id: 'landuse',
      type: 'fill',
      source: 'mapbox-streets',
      'source-layer': 'landuse',
      paint: { 'fill-color': '#0a1320', 'fill-opacity': 1 },
    },
    // Parks / open space — a touch greener so saguaro country reads as desert,
    // not industrial.
    {
      id: 'landuse-park',
      type: 'fill',
      source: 'mapbox-streets',
      'source-layer': 'landuse',
      filter: ['match', ['get', 'class'], ['park','pitch','garden','golf_course','cemetery','wood'], true, false],
      paint: { 'fill-color': '#0d1a18', 'fill-opacity': 0.85 },
    },
    // Sonoran terrain hillshade — pulls the Catalinas, Rincons, Tucson Mts.
    // out of the dark with subtle bronze highlights. This is the visual hook.
    {
      id: 'hillshade',
      type: 'hillshade',
      source: 'mapbox-dem',
      paint: {
        'hillshade-shadow-color': '#000308',
        'hillshade-highlight-color': '#3d2a14',
        'hillshade-accent-color': '#1a0f06',
        'hillshade-exaggeration': 0.55,
      },
    },
    {
      id: 'water',
      type: 'fill',
      source: 'mapbox-streets',
      'source-layer': 'water',
      paint: { 'fill-color': '#021018', 'fill-opacity': 0.95 },
    },
    {
      id: 'waterway',
      type: 'line',
      source: 'mapbox-streets',
      'source-layer': 'waterway',
      paint: { 'line-color': '#0a2438', 'line-width': 1.5, 'line-opacity': 0.7 },
    },
    {
      id: '3d-buildings',
      source: 'mapbox-streets',
      'source-layer': 'building',
      filter: ['==', 'extrude', 'true'],
      type: 'fill-extrusion',
      minzoom: 13,
      paint: {
        // Tall buildings lift into cyan-tinted glass — short ones stay dark.
        // This makes downtown Tucson distinctly readable from afar.
        'fill-extrusion-color': [
          'interpolate', ['linear'], ['get', 'height'],
          0, '#16233b', 20, '#213a5e', 80, '#2d5a8c', 160, '#d4a843', 250, '#00f5ff',
        ],
        'fill-extrusion-height': ['*', ['get', 'height'], 2.2],
        'fill-extrusion-base': ['*', ['get', 'min_height'], 2.2],
        'fill-extrusion-opacity': 0.95,
      },
    },
    // Highway/major roads — CYAN BRAND ACCENT (matches noble3 --accent:#00f5ff)
    // Tinted slightly toward teal at low opacity so they don't blow out the
    // basemap at high zoom. Thicker at city scale for navigation glance.
    {
      id: 'road-highway-casing',
      type: 'line',
      source: 'mapbox-streets',
      'source-layer': 'road',
      filter: ['match', ['get', 'class'], ['motorway','motorway_link','trunk','primary'], true, false],
      paint: {
        'line-color': '#003a4a',
        'line-width': ['interpolate', ['linear'], ['zoom'], 5, 2, 15, 6.5],
        'line-opacity': 0.7,
      },
    },
    {
      id: 'road-highway',
      type: 'line',
      source: 'mapbox-streets',
      'source-layer': 'road',
      filter: ['match', ['get', 'class'], ['motorway','motorway_link','trunk','primary','secondary','tertiary'], true, false],
      paint: {
        'line-color': '#00d5e8',
        'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.8, 15, 3.5],
        'line-opacity': ['interpolate', ['linear'], ['zoom'], 5, 0.55, 12, 0.85, 16, 0.95],
      },
    },
    // Local streets — bronze/copper so they're warm against the cool basemap
    // without competing with the cyan highways for attention.
    {
      id: 'road-street',
      type: 'line',
      source: 'mapbox-streets',
      'source-layer': 'road',
      filter: ['match', ['get', 'class'], ['street','street_limited','pedestrian','path'], true, false],
      paint: {
        'line-color': '#475569',
        'line-width': ['interpolate', ['linear'], ['zoom'], 12, 1.0, 16, 2.5],
        'line-opacity': 0.85,
      },
    },
    // Road labels — muted bronze, subordinate to POIs.
    {
      id: 'road-label',
      type: 'symbol',
      source: 'mapbox-streets',
      'source-layer': 'road',
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
        'text-size': 11,
        'symbol-placement': 'line',
        'text-transform': 'uppercase',
        'text-letter-spacing': 0.08,
      },
      paint: {
        'text-color': '#8a6f4e',
        'text-halo-color': '#03070f',
        'text-halo-width': 1.5,
      },
    },
    // POI labels — emoji prefix + name, larger bold amber (dominant over street labels)
    {
      id: 'poi-label',
      type: 'symbol',
      source: 'mapbox-streets',
      'source-layer': 'poi_label',
      layout: {
        'text-field': [
          'concat',
          ['match', ['downcase', ['coalesce', ['get', 'type'], ['get', 'class'], '']],
            'stadium', '🏟 ',
            'sports_centre', '🏋 ',
            'university', '🎓 ',
            'college', '🎓 ',
            'school', '🏫 ',
            'library', '📚 ',
            'cafe', '☕ ',
            'coffee', '☕ ',
            'bar', '🍺 ',
            'brewery', '🍺 ',
            'pub', '🍺 ',
            'restaurant', '🍽 ',
            'fast_food', '🍔 ',
            'hospital', '🏥 ',
            'pharmacy', '💊 ',
            'bank', '🏦 ',
            'atm', '💳 ',
            'hotel', '🏨 ',
            'motel', '🛏 ',
            'park', '🌲 ',
            'cemetery', '✝ ',
            'church', '⛪ ',
            'place_of_worship', '⛪ ',
            'police', '👮 ',
            'fire_station', '🚒 ',
            'post_office', '📮 ',
            'museum', '🏛 ',
            'theatre', '🎭 ',
            'cinema', '🎬 ',
            'supermarket', '🛒 ',
            'grocery', '🛒 ',
            'gas_station', '⛽ ',
            'fuel', '⛽ ',
            'parking', '🅿 ',
            'airport', '✈ ',
            'bus_station', '🚌 ',
            '📍 '
          ],
          ['get', 'name'],
        ],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 14,
        'text-max-width': 10,
        'text-offset': [0, 0.6],
        'icon-allow-overlap': false,
        'text-allow-overlap': false,
      },
      paint: {
        // Brighter bronze-amber pops against the midnight base. Halo deep navy
        // so the text reads as "neon plaque" rather than "stuck on with glue".
        'text-color': '#f0c870',
        'text-halo-color': '#020610',
        'text-halo-width': 2.5,
        'text-opacity': 0.96,
      },
    },
    // Place labels (city/neighborhood) — small bright cyan, very subtle so they
    // anchor the geography without competing with POIs.
    {
      id: 'place-label',
      type: 'symbol',
      source: 'mapbox-streets',
      'source-layer': 'place_label',
      filter: ['match', ['get', 'type'], ['city','town','neighbourhood','suburb','village'], true, false],
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 8, 11, 14, 14],
        'text-transform': 'uppercase',
        'text-letter-spacing': 0.18,
      },
      paint: {
        'text-color': '#7dd3e8',
        'text-halo-color': '#02060f',
        'text-halo-width': 2.5,
        'text-opacity': 0.85,
      },
    },
  ],
};

const satelliteWesternStyle: any = {
  version: 8,
  name: 'Tucson Cyber-Western Satellite',
  glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
  sources: {
    'mapbox-streets': { type: 'vector', url: 'mapbox://mapbox.mapbox-streets-v8' },
    'mapbox-satellite': { type: 'raster', url: 'mapbox://mapbox.satellite', tileSize: 256 },
    'mapbox-dem': { type: 'raster-dem', url: 'mapbox://mapbox.mapbox-terrain-dem-v1', tileSize: 256, maxzoom: 11 },
  },
  terrain: { source: 'mapbox-dem', exaggeration: 1.5 },
  light: { anchor: 'viewport', color: '#ffb347', intensity: 0.5, position: [1.5, 90, 40] },
  layers: [
    { 
      id: 'satellite', 
      type: 'raster', 
      source: 'mapbox-satellite', 
      paint: { 
        'raster-opacity': 0.8,
        'raster-saturation': -0.4,
        'raster-contrast': 0.25
      } 
    },
    {
      id: '3d-buildings',
      source: 'mapbox-streets',
      'source-layer': 'building',
      type: 'fill-extrusion',
      minzoom: 13,
      paint: {
        'fill-extrusion-color': [
          'interpolate', ['linear'], ['get', 'height'],
          0, '#2d1e15',
          15, '#402a1e',
          40, '#5a3b2a',
          80, '#d4a843',
          180, '#00f5ff'
        ],
        'fill-extrusion-height': [
          'interpolate', ['linear'], ['zoom'],
          13, 0,
          14.5, [
            'case',
            ['has', 'height'], ['*', ['get', 'height'], 2.2],
            ['has', 'min_height'], ['*', ['get', 'min_height'], 3.0],
            22
          ]
        ],
        'fill-extrusion-base': [
          'case',
          ['has', 'min_height'], ['*', ['get', 'min_height'], 2.2],
          0
        ],
        'fill-extrusion-opacity': 0.85,
        'fill-extrusion-vertical-gradient': true,
      },
    },
    {
      id: 'road-highway',
      type: 'line',
      source: 'mapbox-streets',
      'source-layer': 'road',
      filter: ['match', ['get', 'class'], ['motorway','motorway_link','trunk','primary','secondary','tertiary'], true, false],
      paint: {
        // Cyan brand accent over satellite too — keeps visual continuity when
        // operator toggles between map modes.
        'line-color': '#00d5e8',
        'line-width': ['interpolate', ['linear'], ['zoom'], 5, 1, 15, 4],
        'line-opacity': 0.75,
      },
    },
    {
      id: 'road-street',
      type: 'line',
      source: 'mapbox-streets',
      'source-layer': 'road',
      filter: ['match', ['get', 'class'], ['street','street_limited','pedestrian'], true, false],
      paint: { 'line-color': '#8a7a6a', 'line-width': 1.2, 'line-opacity': 0.55 },
    },
    {
      id: 'road-label',
      type: 'symbol',
      source: 'mapbox-streets',
      'source-layer': 'road',
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
        'text-size': 11,
        'symbol-placement': 'line',
        'text-transform': 'uppercase',
        'text-letter-spacing': 0.08,
      },
      paint: { 'text-color': '#ffffff', 'text-halo-color': '#000000', 'text-halo-width': 2 },
    },
    {
      id: 'poi-label',
      type: 'symbol',
      source: 'mapbox-streets',
      'source-layer': 'poi_label',
      layout: {
        'text-field': [
          'concat',
          ['match', ['downcase', ['coalesce', ['get', 'type'], ['get', 'class'], '']],
            'stadium', '🏟 ', 'sports_centre', '🏋 ', 'university', '🎓 ', 'college', '🎓 ',
            'school', '🏫 ', 'library', '📚 ', 'cafe', '☕ ', 'coffee', '☕ ',
            'bar', '🍺 ', 'brewery', '🍺 ', 'pub', '🍺 ', 'restaurant', '🍽 ', 'fast_food', '🍔 ',
            'hospital', '🏥 ', 'pharmacy', '💊 ', 'bank', '🏦 ', 'hotel', '🏨 ',
            'park', '🌲 ', 'church', '⛪ ', 'place_of_worship', '⛪ ',
            'police', '👮 ', 'fire_station', '🚒 ', 'museum', '🏛 ',
            'theatre', '🎭 ', 'cinema', '🎬 ', 'supermarket', '🛒 ',
            'gas_station', '⛽ ', 'fuel', '⛽ ', 'airport', '✈ ',
            '📍 '
          ],
          ['get', 'name'],
        ],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 14,
        'text-max-width': 10,
      },
      paint: { 'text-color': '#ffe88a', 'text-halo-color': '#000000', 'text-halo-width': 2.5 },
    },
  ],
};

export interface EventPin {
  id: string;
  name: string;
  venue?: string;
  lat: number;
  lon: number;
  start_ts?: number;
  category?: string;
  image_url?: string;
  ticket_url?: string;
}

export interface SponsoredPin {
  id: string;
  name: string;
  description?: string;
  lat: number;
  lon: number;
  url?: string;
  emoji: string;
  color: string;
  ends_at: number;
}

interface TacticalMapProps {
  userLocation: { lat: number; lng: number } | null;
  incidents: Incident[];
  onIncidentClick: (incident: Incident) => void;
  onBusinessClick: (feature: any) => void;
  onMapClick: (loc: GeoLocation) => void;
  theme: any;
  highlightedIncidentId?: string | null;
  aryaLocation?: { lat: number; lng: number } | null;
  eventPins?: EventPin[];
  sponsoredPins?: SponsoredPin[];
  venueTarget?: { lat: number; lng: number; name: string } | null;
  onEventClick?: (ev: EventPin) => void;
  children?: React.ReactNode;
}

const TacticalMap: React.FC<TacticalMapProps> = ({
  userLocation,
  incidents,
  onIncidentClick,
  onBusinessClick,
  onMapClick,
  theme,
  highlightedIncidentId,
  aryaLocation,
  eventPins = [],
  sponsoredPins = [],
  venueTarget,
  onEventClick,
  children,
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const styleReady = useRef(false); // true after first style.load; prevents setStyle on mount
  const markersRef = useRef<{ [key: string]: mapboxgl.Marker }>({});
  const userMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const aryaMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const eventMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const sponsorMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const heliMarkersRef = useRef<{ [reg: string]: mapboxgl.Marker }>({});
  const prevIncidentIdsRef = useRef<Set<string>>(new Set());

  // Inject CSS animations for map markers
  useEffect(() => {
    if (document.getElementById('ts-marker-animations')) return;
    const style = document.createElement('style');
    style.id = 'ts-marker-animations';
    style.textContent = `
      @keyframes pulse-purple { 0%,100% { box-shadow: 0 0 12px #a855f760, 0 0 24px #a855f720; } 50% { box-shadow: 0 0 20px #a855f7a0, 0 0 40px #a855f740; } }
      @keyframes pulse-green { 0%,100% { box-shadow: 0 0 12px #22c55e60; } 50% { box-shadow: 0 0 20px #22c55ea0, 0 0 40px #22c55e40; } }
      @keyframes pulse-amber { 0%,100% { box-shadow: 0 0 12px #f59e0b60; } 50% { box-shadow: 0 0 20px #f59e0ba0, 0 0 40px #f59e0b40; } }
      @keyframes pulse-red { 0%,100% { box-shadow: 0 0 12px #ef444460; } 50% { box-shadow: 0 0 20px #ef4444a0, 0 0 40px #ef444440; } }
      @keyframes pulse-cyan { 0%,100% { box-shadow: 0 0 12px #22d3ee60; } 50% { box-shadow: 0 0 20px #22d3eea0, 0 0 40px #22d3ee40; } }
      @keyframes pulse-pink { 0%,100% { box-shadow: 0 0 12px #ec489960; } 50% { box-shadow: 0 0 20px #ec4899a0, 0 0 40px #ec489940; } }
    `;
    document.head.appendChild(style);
  }, []);
  // Latest-incidents ref so click handlers (registered once) see fresh data, not the closure value from mount
  const incidentsRef = useRef<Incident[]>(incidents);
  useEffect(() => { incidentsRef.current = incidents; }, [incidents]);
  const [showEvents, setShowEvents] = useState(true);
  const [showHelis, setShowHelis] = useState<boolean>(() => {
    try { return localStorage.getItem('tucsonstreets:helicopter_overlay') !== 'false'; } catch { return true; }
  });
  useEffect(() => {
    const apply = (next?: boolean) => {
      if (typeof next === 'boolean') { setShowHelis(next); return; }
      try { setShowHelis(localStorage.getItem('tucsonstreets:helicopter_overlay') !== 'false'); } catch {}
    };
    const onCustom = (e: Event) => apply((e as CustomEvent<boolean>).detail);
    const onStorage = (e: StorageEvent) => { if (e.key === 'tucsonstreets:helicopter_overlay') apply(); };
    window.addEventListener('tg:heli-pref', onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('tg:heli-pref', onCustom);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  const [helis, setHelis] = useState<Array<{
    reg: string; label: string; model: string; hex: string;
    flight: string | null; lat: number; lon: number;
    alt_ft: number | null; ground: boolean;
    heading: number | null; speed_kt: number | null;
    vert_rate: number | null; seen: number | null;
  }>>([]);
  const [isSatellite, setIsSatellite] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  // Phase 3: corroborated community events — distinct from individual posts
  const [corroboratedEvents, setCorroboratedEvents] = useState<CorroboratedEvent[]>([]);
  const corroboratedEventsRef = useRef<CorroboratedEvent[]>([]);
  useEffect(() => { corroboratedEventsRef.current = corroboratedEvents; }, [corroboratedEvents]);

  // Extracted so it can be called after setStyle wipes layers
  const addIncidentLayers = (m: mapboxgl.Map) => {
    if (m.getSource('incidents')) return; // already present
    m.addSource('incidents', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50,
    });
    m.addLayer({ id: 'clusters', type: 'circle', source: 'incidents',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': ['step', ['get', 'point_count'], '#51bbd6', 10, '#f1f075', 30, '#f28cb1'],
        'circle-radius': ['step', ['get', 'point_count'], 20, 10, 30, 30, 40],
      },
    });
    m.addLayer({ id: 'cluster-count', type: 'symbol', source: 'incidents',
      filter: ['has', 'point_count'],
      layout: { 'text-field': '{point_count_abbreviated}', 'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'], 'text-size': 12 },
    });
    // ── Animated pulse ring for CRITICAL/HIGH incidents ──
    m.addLayer({ id: 'unclustered-point-pulse', type: 'circle', source: 'incidents',
      filter: ['all', ['!', ['has', 'point_count']], ['in', ['get', 'threatLevel'], ['literal', ['CRITICAL', 'HIGH']]]],
      paint: {
        'circle-color': 'transparent',
        'circle-radius': ['match', ['get', 'threatLevel'], 'CRITICAL', 22, 'HIGH', 16, 0],
        'circle-stroke-width': 2,
        'circle-stroke-color': ['match', ['get', 'threatLevel'], 'CRITICAL', '#ef4444', '#f97316'],
        'circle-stroke-opacity': 0.3,
      },
    });

    // ── Outer glow ring ──
    m.addLayer({ id: 'unclustered-point-halo', type: 'circle', source: 'incidents',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': ['match', ['get', 'threatLevel'], 'CRITICAL', '#ef4444', 'HIGH', '#f97316', 'MEDIUM', '#fbbf24', '#34d399'],
        'circle-radius': ['match', ['get', 'threatLevel'], 'CRITICAL', 14, 'HIGH', 12, 'MEDIUM', 10, 8],
        'circle-opacity': 0.15,
        'circle-blur': 1,
      },
    });

    // ── Main incident dot — larger, with category-based inner glow ──
    m.addLayer({ id: 'unclustered-point', type: 'circle', source: 'incidents',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': ['match', ['get', 'threatLevel'], 'CRITICAL', '#ef4444', 'HIGH', '#f97316', 'MEDIUM', '#fbbf24', '#34d399'],
        'circle-radius': ['case', ['==', ['get', 'source'], 'USER_REPORT'], 7, ['match', ['get', 'threatLevel'], 'CRITICAL', 8, 'HIGH', 7, 'MEDIUM', 6, 5]],
        'circle-stroke-width': ['case', ['==', ['get', 'source'], 'USER_REPORT'], 3, 2],
        'circle-stroke-color': ['case', ['==', ['get', 'source'], 'USER_REPORT'], '#22d3ee', '#ffffff'],
        'circle-opacity': 0.95,
      },
    });

    // ── Animated pulse for CRITICAL incidents (expand/contract) ──
    let pulsePhase = 0;
    const animatePulse = () => {
      pulsePhase = (pulsePhase + 0.03) % (Math.PI * 2);
      const pulseRadius = 18 + Math.sin(pulsePhase) * 6;
      const pulseOpacity = 0.15 + Math.sin(pulsePhase) * 0.15;
      try {
        if (m.getLayer('unclustered-point-pulse')) {
          m.setPaintProperty('unclustered-point-pulse', 'circle-radius', ['match', ['get', 'threatLevel'], 'CRITICAL', pulseRadius, 'HIGH', pulseRadius * 0.8, 0]);
          m.setPaintProperty('unclustered-point-pulse', 'circle-stroke-opacity', pulseOpacity);
        }
      } catch {}
      requestAnimationFrame(animatePulse);
    };
    animatePulse();
    m.addLayer({ id: 'incident-labels', type: 'symbol', source: 'incidents',
      filter: ['!', ['has', 'point_count']],
      layout: {
        'text-field': ['get', 'type'], 'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 10, 'text-offset': [0, -1.8], 'text-anchor': 'bottom',
        'text-transform': 'uppercase', 'text-letter-spacing': 0.08, 'text-max-width': 14,
      },
      paint: {
        'text-color': ['match', ['get', 'threatLevel'], 'CRITICAL', '#fca5a5', 'HIGH', '#fdba74', 'MEDIUM', '#fde68a', '#6ee7b7'],
        'text-halo-color': '#000000', 'text-halo-width': 1.5,
      },
    });

    // ── Corroborated Community Events ──────────────────────────────────────
    // Distinct multi-pin badge — concentric cyan rings, much larger than the
    // single-post USER_REPORT dot, with a "⊕ N reporters" label. When notInCad
    // is true, the outer stroke shifts amber to flag scanner-blind events.
    if (!m.getSource('corroborated_events')) {
      m.addSource('corroborated_events', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      // Outer ring — wide cyan halo (or amber when notInCad)
      m.addLayer({
        id: 'corroborated-event-outer',
        type: 'circle',
        source: 'corroborated_events',
        paint: {
          'circle-radius': 22,
          'circle-color': '#22d3ee',
          'circle-opacity': 0.18,
          'circle-stroke-width': 2,
          'circle-stroke-color': ['case', ['==', ['get', 'notInCad'], true], '#fbbf24', '#22d3ee'],
          'circle-stroke-opacity': 0.95,
        },
      });
      // Inner ring — solid cyan core
      m.addLayer({
        id: 'corroborated-event-inner',
        type: 'circle',
        source: 'corroborated_events',
        paint: {
          'circle-radius': 14,
          'circle-color': '#0891b2',
          'circle-opacity': 0.55,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#67e8f9',
          'circle-stroke-opacity': 1,
        },
      });
      // Center symbol — multi-reporter badge (concentric mark)
      m.addLayer({
        id: 'corroborated-event-symbol',
        type: 'symbol',
        source: 'corroborated_events',
        layout: {
          'text-field': '⊕',
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 16,
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#0e7490',
          'text-halo-width': 1.5,
        },
      });
      // Reporter-count label below the marker
      m.addLayer({
        id: 'corroborated-event-label',
        type: 'symbol',
        source: 'corroborated_events',
        layout: {
          'text-field': ['concat', ['to-string', ['get', 'reporterCount']], ' reporters'],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 10,
          'text-offset': [0, 2.2],
          'text-anchor': 'top',
          'text-transform': 'uppercase',
          'text-letter-spacing': 0.1,
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': ['case', ['==', ['get', 'notInCad'], true], '#fde68a', '#a5f3fc'],
          'text-halo-color': '#000000',
          'text-halo-width': 1.6,
        },
      });
    }
  };

  // Initialize Map
  useEffect(() => {
    if (map.current || !mapContainer.current) return;

    const mapInstance = new mapboxgl.Map({
      container: mapContainer.current,
      style: modernWesternStyle,
      center: [-110.9747, 32.2226],
      zoom: 15.5,
      pitch: 55,
      bearing: 30,
      antialias: true,
    });

    mapInstance.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
    mapInstance.addControl(new mapboxgl.FullscreenControl(), 'bottom-right');

    mapInstance.on('load', () => {
      const loc = userLocation || { lat: 32.2226, lng: -110.9747 };
      const introPlayed = sessionStorage.getItem('ts_intro');

      mapInstance.flyTo({ center: [-110.9747, 32.2226], zoom: 15.8, pitch: 55, bearing: 25, duration: 1000 });

      // Incident data source & layers
      addIncidentLayers(mapInstance);
      styleReady.current = true;

      mapInstance.on('click', 'clusters', (e) => {
        const features = mapInstance.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        const clusterId = (features[0].properties as any).cluster_id;
        const source = mapInstance.getSource('incidents') as mapboxgl.GeoJSONSource;
        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          mapInstance.easeTo({ center: (features[0].geometry as any).coordinates, zoom: (zoom ?? 14) + 1 });
        });
      });

      mapInstance.on('mouseenter', 'clusters', () => { mapInstance.getCanvas().style.cursor = 'pointer'; });
      mapInstance.on('mouseleave', 'clusters', () => { mapInstance.getCanvas().style.cursor = ''; });

      mapInstance.on('click', 'poi-label', (e) => {
        e.originalEvent.stopPropagation();
        if (e.features?.length) onBusinessClick(e.features[0]);
      });

      mapInstance.on('click', 'unclustered-point', (e) => {
        e.originalEvent.stopPropagation();
        if (e.features?.length) {
          const id = e.features![0].properties!.id;
          // Use ref so we always read the freshest incidents list, not the one captured at mount
          const inc = incidentsRef.current.find(i => i.id === id);
          if (inc) onIncidentClick(inc);
        }
      });

      // Corroborated event click — temporary alert until detail panel lands in Phase 5+
      const onCorroboratedClick = (e: any) => {
        e.originalEvent?.stopPropagation();
        const f = e.features?.[0];
        if (!f) return;
        const id = f.properties.id;
        const reporterCount = f.properties.reporterCount;
        const ev = corroboratedEventsRef.current.find(x => x.id === id);
        const notInCad = ev?.notInCad ? ' (NOT IN CAD)' : '';
        // Styled popup instead of alert()
        const coords = f.geometry?.coordinates?.slice() as [number, number] | undefined;
        if (coords) {
          new mapboxgl.Popup({ closeButton: true, closeOnClick: true, className: 'ts-event-popup', maxWidth: '280px' })
            .setLngLat(coords)
            .setHTML(`
              <div style="background:#110a1c;padding:14px;border-radius:10px;border:1px solid rgba(34,211,238,0.3);font-family:ui-monospace,monospace">
                <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px">
                  <div style="width:8px;height:8px;border-radius:50%;background:${ev?.notInCad ? '#f97316' : '#22d3ee'};box-shadow:0 0 8px ${ev?.notInCad ? '#f97316' : '#22d3ee'}"></div>
                  <span style="color:#22d3ee;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em">COMMUNITY EVENT #${id}</span>
                </div>
                <div style="color:#e8eaf0;font-size:13px;font-weight:600;margin-bottom:4px">${reporterCount} reporter${reporterCount > 1 ? 's' : ''} corroborated</div>
                ${notInCad ? '<div style="color:#f97316;font-size:10px;margin-bottom:4px;font-weight:700">⚠ NOT IN POLICE CAD — scanner-blind event</div>' : ''}
                <div style="color:#8a93a8;font-size:10px">Click the map to dismiss</div>
              </div>
            `)
            .addTo(mapInstance);
        }
      };
      mapInstance.on('click', 'corroborated-event-outer', onCorroboratedClick);
      mapInstance.on('click', 'corroborated-event-inner', onCorroboratedClick);
      mapInstance.on('click', 'corroborated-event-symbol', onCorroboratedClick);
      const setCorroCursor = (cursor: string) => () => { mapInstance.getCanvas().style.cursor = cursor; };
      mapInstance.on('mouseenter', 'corroborated-event-outer', setCorroCursor('pointer'));
      mapInstance.on('mouseleave', 'corroborated-event-outer', setCorroCursor(''));
      mapInstance.on('mouseenter', 'corroborated-event-inner', setCorroCursor('pointer'));
      mapInstance.on('mouseleave', 'corroborated-event-inner', setCorroCursor(''));

      const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false });
      mapInstance.on('mouseenter', 'unclustered-point', (e) => {
        if (!mapInstance.getLayer('unclustered-point')) return;
        mapInstance.getCanvas().style.cursor = 'pointer';
        const feature = e.features![0];
        const coords = (feature.geometry as any).coordinates.slice();
        const p = feature.properties!;
        const dist = userLocation ? calculateDistance(userLocation.lat, userLocation.lng, p.lat, p.lng) : 'N/A';
        popup.setLngLat(coords).setHTML(`<div class="p-2 text-xs"><p><strong>Type:</strong> ${p.type}</p><p><strong>Threat:</strong> ${p.threatLevel}</p><p><strong>Distance:</strong> ${dist} km</p></div>`).addTo(mapInstance);
      });
      mapInstance.on('mouseleave', 'unclustered-point', () => {
        if (!mapInstance.getLayer('unclustered-point')) return;
        mapInstance.getCanvas().style.cursor = '';
        popup.remove();
      });

      setMapLoaded(true);
    });

    mapInstance.on('click', (e) => { onMapClick({ lat: e.lngLat.lat, lng: e.lngLat.lng }); });

    // Glitch effect on zoom — add CSS class briefly
    mapInstance.on('zoom', () => {
      const canvas = mapInstance.getCanvas();
      canvas.classList.add('map-glitch');
      setTimeout(() => canvas.classList.remove('map-glitch'), 350);
    });

    map.current = mapInstance;
    return () => { if (map.current) { map.current.remove(); map.current = null; } };
  }, []);

  // ── Idle orbit mode — slow camera rotation when inactive ──
  useEffect(() => {
    if (!mapLoaded || !map.current) return;
    let lastInteraction = Date.now();
    let orbitActive = false;
    let orbitRaf: number | null = null;

    const resetIdle = () => { lastInteraction = Date.now(); orbitActive = false; };
    const m = map.current;
    m.on('mousedown', resetIdle);
    m.on('touchstart', resetIdle);
    m.on('wheel', resetIdle);

    const checkIdle = () => {
      if (Date.now() - lastInteraction > 90000 && !orbitActive && m) { // 90s idle
        orbitActive = true;
        const orbit = () => {
          if (!orbitActive || !m) return;
          m.rotateTo((m.getBearing() + 0.15) % 360, { duration: 0 });
          orbitRaf = requestAnimationFrame(orbit);
        };
        orbit();
      }
    };
    const idleInterval = setInterval(checkIdle, 5000);

    return () => {
      clearInterval(idleInterval);
      if (orbitRaf) cancelAnimationFrame(orbitRaf);
      m.off('mousedown', resetIdle);
      m.off('touchstart', resetIdle);
      m.off('wheel', resetIdle);
    };
  }, [mapLoaded]);

  // Style swap — only runs when isSatellite actually toggles, not on initial mount
  useEffect(() => {
    if (!styleReady.current || !map.current) return;
    const m = map.current;
    m.setStyle(isSatellite ? satelliteWesternStyle : modernWesternStyle);
    m.once('style.load', () => {
      addIncidentLayers(m);
      // Re-populate incidents
      const src = m.getSource('incidents') as mapboxgl.GeoJSONSource | undefined;
      if (src) {
        src.setData({
          type: 'FeatureCollection',
          features: incidents.map(inc => ({
            type: 'Feature',
            properties: { id: inc.id, type: inc.type, threatLevel: inc.threatLevel, source: inc.source, lat: inc.location.lat, lng: inc.location.lng },
            geometry: { type: 'Point', coordinates: [inc.location.lng, inc.location.lat] },
          })),
        });
      }
      // Re-populate corroborated events
      const csrc = m.getSource('corroborated_events') as mapboxgl.GeoJSONSource | undefined;
      if (csrc) {
        csrc.setData({
          type: 'FeatureCollection',
          features: corroboratedEventsRef.current.map(e => ({
            type: 'Feature',
            properties: { id: e.id, reporterCount: e.reporterCount, postCount: e.postCount, notInCad: e.notInCad },
            geometry: { type: 'Point', coordinates: [e.location.lng, e.location.lat] },
          })),
        });
      }
    });
  }, [isSatellite]); // intentionally omit mapLoaded — styleReady ref guards against early runs

  // Update incident source + Three.js spikes + pulse rings for new arrivals
  useEffect(() => {
    if (!mapLoaded || !map.current) return;

    const source = map.current.getSource('incidents') as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData({
        type: 'FeatureCollection',
        features: incidents.map(inc => ({
          type: 'Feature',
          properties: { id: inc.id, type: inc.type, threatLevel: inc.threatLevel, source: inc.source, lat: inc.location.lat, lng: inc.location.lng },
          geometry: { type: 'Point', coordinates: [inc.location.lng, inc.location.lat] },
        })),
      });
    }

    // Detect new CRITICAL/HIGH incidents and animate attention
    const newIds = new Set(incidents.map(i => i.id));
    const prevIds = prevIncidentIdsRef.current;
    if (prevIds.size > 0) {
      for (const inc of incidents) {
        if (!prevIds.has(inc.id) && (inc.threatLevel === 'CRITICAL' || inc.threatLevel === 'HIGH')) {
          // Flash the map border red briefly
          const canvas = map.current.getCanvas();
          canvas.style.boxShadow = inc.threatLevel === 'CRITICAL'
            ? '0 0 40px rgba(239,68,68,0.6) inset, 0 0 80px rgba(239,68,68,0.3) inset'
            : '0 0 30px rgba(249,115,22,0.4) inset';
          setTimeout(() => { canvas.style.boxShadow = ''; }, 2000);

          // Fly to the incident if it's within 5 miles of user
          if (userLocation) {
            const dist = Math.sqrt(
              (inc.location.lat - userLocation.lat) ** 2 +
              (inc.location.lng - userLocation.lng) ** 2
            ) * 69; // rough miles
            if (dist < 5 && inc.threatLevel === 'CRITICAL') {
              map.current.flyTo({
                center: [inc.location.lng, inc.location.lat],
                zoom: Math.max(map.current.getZoom(), 15),
                speed: 1.5,
                essential: false,
              });
            }
          }
          break; // only animate for the first new critical
        }
      }
    }

    prevIncidentIdsRef.current = newIds;
  }, [incidents, mapLoaded]);

  // ── Corroborated community events: fetch + 60s poll + SSE subscription ──
  useEffect(() => {
    let cancelled = false;
    let pollId: number | null = null;
    let es: EventSource | null = null;

    const load = async () => {
      try {
        const r = await eventsApi.list('active', 50);
        if (!cancelled) setCorroboratedEvents(r.events || []);
      } catch {
        // network/api errors are non-fatal — markers just won't update this tick
      }
    };

    load();
    pollId = window.setInterval(load, 60_000);

    // SSE — direct EventSource (sseHub.ts not yet present in services/)
    const upsert = (ev: CorroboratedEvent) => {
      setCorroboratedEvents(prev => {
        const idx = prev.findIndex(p => p.id === ev.id);
        if (idx === -1) return [...prev, ev];
        const copy = prev.slice();
        copy[idx] = { ...copy[idx], ...ev };
        return copy;
      });
    };
    const remove = (id: number) => {
      setCorroboratedEvents(prev => prev.filter(p => p.id !== id));
    };

    try {
      es = new EventSource('/api/stream/posts');
      const handle = (parser: (data: any) => void) => (msg: MessageEvent) => {
        try { parser(JSON.parse(msg.data)); } catch {}
      };
      es.addEventListener('event.created', handle((d) => { if (d?.event) upsert(d.event); }));
      es.addEventListener('event.updated', handle((d) => { if (d?.event) upsert(d.event); }));
      es.addEventListener('event.closed',  handle((d) => {
        const id = d?.event?.id ?? d?.id;
        if (typeof id === 'number') remove(id);
      }));
    } catch {
      // SSE not available — polling still keeps state fresh
    }

    return () => {
      cancelled = true;
      if (pollId != null) window.clearInterval(pollId);
      if (es) es.close();
    };
  }, []);

  // Push corroborated events into the GeoJSON source whenever state changes
  useEffect(() => {
    if (!mapLoaded || !map.current) return;
    const source = map.current.getSource('corroborated_events') as mapboxgl.GeoJSONSource | undefined;
    if (!source) return;
    source.setData({
      type: 'FeatureCollection',
      features: corroboratedEvents.map(e => ({
        type: 'Feature',
        properties: {
          id: e.id,
          reporterCount: e.reporterCount,
          postCount: e.postCount,
          notInCad: e.notInCad,
        },
        geometry: { type: 'Point', coordinates: [e.location.lng, e.location.lat] },
      })),
    });
  }, [corroboratedEvents, mapLoaded, isSatellite]);

  // User location marker
  useEffect(() => {
    if (!mapLoaded || !map.current || !userLocation) return;
    if (!userMarkerRef.current) {
      const el = document.createElement('div');
      el.className = 'w-5 h-5 rounded-full border-2 border-white shadow-[0_0_15px_rgba(59,130,246,0.8)]';
      el.style.backgroundColor = '#3b82f6';
      userMarkerRef.current = new mapboxgl.Marker({ element: el }).setLngLat([userLocation.lng, userLocation.lat]).addTo(map.current);
      map.current.flyTo({ center: [userLocation.lng, userLocation.lat], zoom: 16 });
    } else {
      userMarkerRef.current.setLngLat([userLocation.lng, userLocation.lat]);
    }
  }, [userLocation, mapLoaded]);

  // Arya paw tracks
  useEffect(() => {
    if (!mapLoaded || !map.current || !aryaLocation) return;
    aryaMarkersRef.current.forEach(m => m.remove());
    aryaMarkersRef.current = [];
    const trail = 5;
    for (let i = 0; i < trail; i++) {
      const frac = i / (trail - 1);
      const spread = 0.002;
      const angle = Math.random() * Math.PI * 2;
      const dist = (1 - frac) * spread;
      const lat = aryaLocation.lat + Math.sin(angle) * dist;
      const lng = aryaLocation.lng + Math.cos(angle) * dist;
      const el = document.createElement('div');
      el.innerHTML = '🐾';
      el.style.cssText = `font-size:${14 + frac * 8}px;opacity:${0.3 + frac * 0.7};cursor:pointer;filter:drop-shadow(0 0 4px rgba(255,200,0,0.6));transform:rotate(${Math.random() * 40 - 20}deg);transition:transform 0.2s;user-select:none;`;
      if (i === trail - 1) {
        el.style.fontSize = '28px';
        el.style.filter = 'drop-shadow(0 0 8px rgba(255,200,0,1))';
        const popup = new mapboxgl.Popup({ offset: 25, closeButton: false })
          .setHTML(`<div style="font-family:monospace;font-size:11px;color:#ffcc00;background:#0a0a0a;padding:8px;border-radius:4px;border:1px solid #ffcc0044;max-width:200px"><b>🐾 ARYA — K9 TRACKING</b><br/><span style="color:#aaa">Last scanner contact at this location.<br/>She's hunting someone. Follow the calls.</span></div>`);
        aryaMarkersRef.current.push(new mapboxgl.Marker({ element: el }).setLngLat([lng, lat]).setPopup(popup).addTo(map.current!));
      } else {
        aryaMarkersRef.current.push(new mapboxgl.Marker({ element: el }).setLngLat([lng, lat]).addTo(map.current!));
      }
    }
    map.current.easeTo({ center: [aryaLocation.lng, aryaLocation.lat], zoom: Math.max(map.current.getZoom(), 14), duration: 2000 });
  }, [aryaLocation, mapLoaded]);

  // Event pins
  useEffect(() => {
    if (!mapLoaded || !map.current) return;
    eventMarkersRef.current.forEach(m => m.remove());
    eventMarkersRef.current = [];
    if (!showEvents) return;
    for (const ev of eventPins) {
      const el = document.createElement('div');
      const cat = (ev.category || '').toLowerCase();
      const hasImage = ev.image_url && ev.image_url.startsWith('http');

      // Category config: emoji, gradient colors, animation
      const catConfig: Record<string, { emoji: string; grad: string; glow: string; anim: string }> = {
        sport:   { emoji: '🏟️', grad: 'linear-gradient(135deg,#1a3a1a,#0d2b0d)', glow: '#22c55e', anim: 'pulse-green 2s infinite' },
        music:   { emoji: '🎵', grad: 'linear-gradient(135deg,#2d1a3a,#1a0d2b)', glow: '#a855f7', anim: 'pulse-purple 1.5s infinite' },
        concert: { emoji: '🎸', grad: 'linear-gradient(135deg,#2d1a3a,#1a0d2b)', glow: '#a855f7', anim: 'pulse-purple 1.5s infinite' },
        art:     { emoji: '🎨', grad: 'linear-gradient(135deg,#3a2a1a,#2b1a0d)', glow: '#f59e0b', anim: 'pulse-amber 2.5s infinite' },
        food:    { emoji: '🍽️', grad: 'linear-gradient(135deg,#3a1a1a,#2b0d0d)', glow: '#ef4444', anim: 'pulse-red 2s infinite' },
        comedy:  { emoji: '😂', grad: 'linear-gradient(135deg,#3a3a1a,#2b2b0d)', glow: '#eab308', anim: 'pulse-amber 1.8s infinite' },
        family:  { emoji: '👨‍👩‍👧', grad: 'linear-gradient(135deg,#1a2a3a,#0d1a2b)', glow: '#22d3ee', anim: 'pulse-cyan 2s infinite' },
        theatre: { emoji: '🎭', grad: 'linear-gradient(135deg,#3a1a2a,#2b0d1a)', glow: '#ec4899', anim: 'pulse-pink 2s infinite' },
        default: { emoji: '📅', grad: 'linear-gradient(135deg,#1a1025,#0d0818)', glow: '#a855f7', anim: 'pulse-purple 2s infinite' },
      };
      const cfg = Object.entries(catConfig).find(([k]) => cat.includes(k))?.[1] ?? catConfig.default;

      if (hasImage) {
        // Rich image marker with venue photo
        el.style.cssText = `
          width:52px;height:52px;border-radius:12px;overflow:hidden;
          border:2px solid ${cfg.glow};cursor:pointer;position:relative;
          box-shadow:0 0 12px ${cfg.glow}60,0 2px 8px rgba(0,0,0,.5);
          transition:transform .2s,box-shadow .2s;
          animation:${cfg.anim};
        `;
        el.innerHTML = `
          <img src="${ev.image_url}" style="width:100%;height:100%;object-fit:cover;" loading="lazy" alt="${ev.name}" onerror="this.style.display='none';this.nextElementSibling.style.display='flex'">
          <div style="display:none;width:100%;height:100%;background:${cfg.grad};align-items:center;justify-content:center;font-size:22px">${cfg.emoji}</div>
          <div style="position:absolute;bottom:-2px;right:-2px;background:${cfg.glow};color:#000;font-size:8px;font-weight:800;padding:1px 4px;border-radius:4px;text-transform:uppercase">${cat.includes('music') ? 'LIVE' : cat.includes('sport') ? 'GAME' : cat.includes('food') ? 'EAT' : 'EVENT'}</div>
        `;
      } else {
        // Animated emoji marker (no image)
        el.style.cssText = `
          width:44px;height:44px;border-radius:50%;
          background:${cfg.grad};border:2px solid ${cfg.glow};
          display:flex;align-items:center;justify-content:center;
          font-size:20px;cursor:pointer;position:relative;
          box-shadow:0 0 14px ${cfg.glow}50,0 0 28px ${cfg.glow}20;
          transition:transform .2s;animation:${cfg.anim};
        `;
        el.innerHTML = cfg.emoji;
      }

      // Hover tooltip with event name
      el.onmouseenter = () => {
        el.style.transform = 'scale(1.25)';
        el.style.zIndex = '10';
        el.title = `${ev.name}${ev.venue ? ' @ ' + ev.venue : ''}`;
      };
      el.onmouseleave = () => { el.style.transform = 'scale(1)'; el.style.zIndex = ''; };
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        if (onEventClick) onEventClick(ev);
      });
      eventMarkersRef.current.push(new mapboxgl.Marker({ element: el }).setLngLat([ev.lon, ev.lat]).addTo(map.current!));
    }
  }, [eventPins, mapLoaded, showEvents, onEventClick]);

  // Sponsored pins — Custom Storefront 3D Image Beacons ($25/mo)
  useEffect(() => {
    if (!mapLoaded || !map.current) return;
    sponsorMarkersRef.current.forEach(m => m.remove());
    sponsorMarkersRef.current = [];

    // Ensure CSS animation styles exist
    if (!document.getElementById('ts-sponsor-beacon-styles')) {
      const styleEl = document.createElement('style');
      styleEl.id = 'ts-sponsor-beacon-styles';
      styleEl.textContent = `
        @keyframes beaconFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-7px); }
        }
        @keyframes beaconPulseRing {
          0% { transform: scale(0.6); opacity: 0.9; }
          100% { transform: scale(2.4); opacity: 0; }
        }
      `;
      document.head.appendChild(styleEl);
    }

    for (const pin of sponsoredPins) {
      const container = document.createElement('div');
      container.style.cssText = 'position:relative;display:flex;flex-direction:column;align-items:center;cursor:pointer;user-select:none;pointer-events:auto;';

      // 1. Floating Storefront Card
      const card = document.createElement('div');
      card.style.cssText = `
        background: rgba(10, 14, 23, 0.96);
        border: 2px solid #ffd700;
        border-radius: 8px;
        padding: 5px 8px;
        box-shadow: 0 0 20px rgba(255, 215, 0, 0.5), 0 8px 24px rgba(0,0,0,0.8);
        display: flex;
        align-items: center;
        gap: 6px;
        animation: beaconFloat 3s ease-in-out infinite;
        transition: transform 0.2s, box-shadow 0.2s;
        white-space: nowrap;
      `;
      card.innerHTML = `
        <span style="font-size: 16px;">${pin.emoji || '⭐'}</span>
        <div style="display: flex; flex-direction: column;">
          <span style="font-family: 'Orbitron', monospace; font-size: 10px; font-weight: 800; color: #ffffff; letter-spacing: 0.5px;">${pin.name}</span>
          <span style="font-family: monospace; font-size: 8px; color: #ffd700; font-weight: bold;">★ $25/MO BEACON</span>
        </div>
      `;
      card.onmouseenter = () => {
        card.style.transform = 'scale(1.15) translateY(-4px)';
        card.style.boxShadow = '0 0 30px rgba(255, 215, 0, 0.8), 0 12px 30px rgba(0,0,0,0.9)';
      };
      card.onmouseleave = () => {
        card.style.transform = 'none';
        card.style.boxShadow = '0 0 20px rgba(255, 215, 0, 0.5), 0 8px 24px rgba(0,0,0,0.8)';
      };
      container.appendChild(card);

      // 2. Vertical Light Stem
      const stem = document.createElement('div');
      stem.style.cssText = 'width: 2px; height: 16px; background: linear-gradient(to bottom, #ffd700, rgba(255,215,0,0.2));';
      container.appendChild(stem);

      // 3. Pulsing Ground Radar Ring
      const base = document.createElement('div');
      base.style.cssText = 'position: relative; width: 12px; height: 12px; border-radius: 50%; background: #ffd700;';
      const pulseRing = document.createElement('div');
      pulseRing.style.cssText = 'position: absolute; inset: -8px; border-radius: 50%; border: 2px solid #ffd700; animation: beaconPulseRing 2s ease-out infinite; pointer-events: none;';
      base.appendChild(pulseRing);
      container.appendChild(base);

      const endsStr = new Date(pin.ends_at * 1000).toLocaleDateString('en-US', { month:'short', day:'numeric', timeZone:'America/Phoenix' });
      const popup = new mapboxgl.Popup({ offset: 35, closeButton: false, maxWidth: '280px' })
        .setHTML(`
          <div style="font-family: monospace; font-size: 11px; background: #080c14; padding: 12px; border-radius: 8px; border: 2px solid #ffd700; color: #f5f5f5; box-shadow: 0 0 25px rgba(255,215,0,0.4);">
            <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom: 6px;">
              <span style="font-weight: 800; color: #ffd700; font-size: 13px;">${pin.emoji || '⭐'} ${pin.name}</span>
              <span style="background: rgba(255,215,0,0.2); color: #ffd700; font-size: 9px; padding: 2px 6px; border-radius: 4px; font-weight: bold;">$25/MO</span>
            </div>
            ${pin.description ? `<div style="color: #cbd5e1; margin-bottom: 8px; line-height: 1.4; font-size: 11px;">${pin.description}</div>` : ''}
            <div style="display:flex; justify-content:space-between; align-items:center; margin-top: 8px; border-top: 1px solid rgba(255,255,255,0.1); pt-2;">
              ${pin.url ? `<a href="${pin.url}" target="_blank" rel="noopener" style="color: #38bdf8; text-decoration: underline; font-weight: bold;">Open Link &rarr;</a>` : '<span></span>'}
              <span style="color: #64748b; font-size: 9px;">Verified Active</span>
            </div>
          </div>
        `);

      sponsorMarkersRef.current.push(
        new mapboxgl.Marker({ element: container, anchor: 'bottom' })
          .setLngLat([pin.lon, pin.lat])
          .setPopup(popup)
          .addTo(map.current!)
      );
    }
  }, [sponsoredPins, mapLoaded]);

  // ── Three.js landmarks — GTA-style stylized Tucson landmarks (A-Mountain,
  // etc.) rendered as a persistent custom Mapbox layer. Mounts once on map
  // load, stays for the session. Opt-out: localStorage.tpd_landmarks=off.
  useEffect(() => {
    if (!mapLoaded || !map.current) return;
    if (typeof localStorage !== 'undefined' && localStorage.getItem('tpd_landmarks') === 'off') return;
    const m = map.current;
    mountLandmarks(m);
    return () => { dismountLandmarks(m); };
  }, [mapLoaded]);

  // ── Three.js game scene — geo-anchored 3D scene for the active game round.
  // Mounts a custom Mapbox layer (shared GL context) at the incident location,
  // dismounts when the round ends or the user navigates away. Camera flies to
  // the scene on round start; opt-out by setting localStorage.tpd_3d_game=off.
  useEffect(() => {
    if (!mapLoaded || !map.current) return;
    if (typeof localStorage !== 'undefined' && localStorage.getItem('tpd_3d_game') === 'off') return;
    const m = map.current;
    let currentRoundId: string | null = null;

    const unsub = subscribeToActiveRound((round) => {
      if (!round) {
        dismountGameScene(m);
        currentRoundId = null;
        return;
      }
      if (round.id === currentRoundId) {
        updateGameScene(m, round);
        return;
      }
      currentRoundId = round.id;
      mountGameScene(m, round);
      flyToGameScene(m, round);
    });

    return () => {
      unsub();
      dismountGameScene(m);
    };
  }, [mapLoaded]);

  // TPD Helicopter polling — pulls live ADS-B every 15s from our backend
  useEffect(() => {
    if (!showHelis) { setHelis([]); return; }
    let cancelled = false;
    const pull = async () => {
      try {
        const r = await fetch('/api/helicopter');
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled) setHelis(Array.isArray(j.aircraft) ? j.aircraft : []);
      } catch {}
    };
    pull();
    const id = window.setInterval(pull, 15000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [showHelis]);

  // Helicopter markers on map
  useEffect(() => {
    if (!mapLoaded || !map.current) return;
    const liveRegs = new Set(helis.map(h => h.reg));
    // Remove markers no longer in feed
    Object.keys(heliMarkersRef.current).forEach(reg => {
      if (!liveRegs.has(reg)) {
        heliMarkersRef.current[reg].remove();
        delete heliMarkersRef.current[reg];
      }
    });
    // Add or update
    for (const h of helis) {
      const altStr = h.ground ? 'GROUND' : (h.alt_ft != null ? `${h.alt_ft.toLocaleString()} ft` : '—');
      const spdStr = h.speed_kt != null ? `${Math.round(h.speed_kt)} kt` : '—';
      const vrStr = h.vert_rate != null ? `${h.vert_rate > 0 ? '↑' : h.vert_rate < 0 ? '↓' : '·'} ${Math.abs(h.vert_rate)} fpm` : '';
      const headingDeg = h.heading ?? 0;
      const popupHTML = `
        <div style="font-family:monospace;font-size:11px;background:#0a0a0a;padding:10px;border-radius:6px;border:1px solid #ff3b3b66;color:#f5f5f5;min-width:180px">
          <div style="font-weight:bold;color:#ff5555;font-size:13px;margin-bottom:6px;letter-spacing:0.05em">🚁 ${h.label}</div>
          <div style="color:#aaa">REG: <span style="color:#fff">${h.reg}</span></div>
          ${h.flight ? `<div style="color:#aaa">CALLSIGN: <span style="color:#fff">${h.flight}</span></div>` : ''}
          <div style="color:#aaa">MODEL: <span style="color:#fff">${h.model}</span></div>
          <div style="color:#aaa">ALT: <span style="color:#fff">${altStr}</span></div>
          <div style="color:#aaa">SPD: <span style="color:#fff">${spdStr}</span></div>
          <div style="color:#aaa">HDG: <span style="color:#fff">${headingDeg.toFixed(0)}°</span> ${vrStr ? `<span style="color:#888">${vrStr}</span>` : ''}</div>
          <div style="color:#666;font-size:9px;margin-top:6px">via adsb.lol · ${h.ground ? 'on ground' : 'airborne'}</div>
        </div>`;
      let marker = heliMarkersRef.current[h.reg];
      if (!marker) {
        const el = document.createElement('div');
        el.className = 'tpd-heli-marker';
        el.innerHTML = `
          <div class="tpd-heli-pulse"></div>
          <div class="tpd-heli-body" style="transform:rotate(${headingDeg}deg)">
            <div class="tpd-heli-rotor"></div>
            <div class="tpd-heli-icon">🚁</div>
          </div>
          <div class="tpd-heli-label">${h.label.replace('TPD ','')}</div>`;
        const popup = new mapboxgl.Popup({ offset: 22, closeButton: false, maxWidth: '260px' }).setHTML(popupHTML);
        marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
          .setLngLat([h.lon, h.lat])
          .setPopup(popup)
          .addTo(map.current!);
        heliMarkersRef.current[h.reg] = marker;
      } else {
        marker.setLngLat([h.lon, h.lat]);
        const el = marker.getElement();
        const body = el.querySelector('.tpd-heli-body') as HTMLElement | null;
        if (body) body.style.transform = `rotate(${headingDeg}deg)`;
        marker.getPopup()?.setHTML(popupHTML);
      }
      // Ground vs airborne styling
      const el = marker.getElement();
      el.classList.toggle('tpd-heli-ground', !!h.ground);
    }
  }, [helis, mapLoaded]);

  // Venue fly-to
  useEffect(() => {
    if (!mapLoaded || !map.current || !venueTarget) return;
    map.current.flyTo({ center: [venueTarget.lng, venueTarget.lat], zoom: 16, duration: 1200 });
  }, [venueTarget, mapLoaded]);

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return (R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))).toFixed(1);
  };

  return (
    <div className="absolute inset-0 bg-black overflow-hidden">
      <div ref={mapContainer} className="w-full h-full" />

      {children}

      {/* Map controls */}
      <div className="absolute bottom-20 right-4 z-10 font-mono flex flex-col gap-2">
        <button
          onClick={() => setIsSatellite(!isSatellite)}
          className="bg-black/90 border border-amber-900/50 p-2 rounded-sm text-amber-600 hover:text-amber-400 hover:border-amber-500/50 transition-colors shadow-[0_0_15px_rgba(217,119,6,0.15)] backdrop-blur-sm flex items-center gap-2"
          title="Toggle Satellite View"
        >
          <Layers size={20} />
          <span className="text-xs font-bold uppercase tracking-widest hidden sm:block">{isSatellite ? 'Terrain' : 'Satellite'}</span>
        </button>
        <button
          onClick={() => { if ((window as any).toggleWalkMode) (window as any).toggleWalkMode(); }}
          className="bg-black/90 border border-emerald-900/50 p-2 rounded-sm text-emerald-600 hover:text-emerald-400 hover:border-emerald-500/50 transition-colors shadow-[0_0_15px_rgba(16,185,129,0.15)] backdrop-blur-sm flex items-center gap-2"
          title="Enter First-Person Walk Mode"
        >
          <span className="text-xs font-bold uppercase tracking-widest hidden sm:block">🚶 WALK MODE</span>
        </button>
        <button
          onClick={() => setShowEvents(v => !v)}
          className={`bg-black/90 border p-2 rounded-sm transition-colors backdrop-blur-sm flex items-center gap-2 ${showEvents ? 'border-purple-700/60 text-purple-400' : 'border-gray-800 text-gray-600'}`}
          title="Toggle Events Layer"
        >
          <span className="text-base leading-none">📅</span>
          <span className="text-xs font-bold uppercase tracking-widest hidden sm:block">Events</span>
        </button>
        <button
          onClick={() => {
            const v = !showHelis;
            setShowHelis(v);
            try {
              localStorage.setItem('tucsonstreets:helicopter_overlay', String(v));
              window.dispatchEvent(new CustomEvent('tg:heli-pref', { detail: v }));
            } catch {}
          }}
          className={`bg-black/90 border p-2 rounded-sm transition-colors backdrop-blur-sm flex items-center gap-2 ${showHelis ? 'border-red-700/60 text-red-400' : 'border-gray-800 text-gray-600'}`}
          title="Toggle PIGAIR1 Air Support Overlay"
        >
          <span className="text-base leading-none">🚁</span>
          <span className="text-xs font-bold uppercase tracking-widest hidden sm:block">
            PIGAIR1 {showHelis && helis.length > 0 ? `(${helis.length})` : ''}
          </span>
        </button>
      </div>

      <style>{`
        @keyframes sponsorPulse {
          0%,100% { box-shadow: 0 0 10px rgba(255,215,0,0.5), 0 0 20px rgba(255,215,0,0.2); }
          50%      { box-shadow: 0 0 18px rgba(255,215,0,0.8), 0 0 36px rgba(255,215,0,0.4); }
        }
        /* TPD helicopter marker */
        .tpd-heli-marker {
          position: relative;
          width: 44px; height: 44px;
          cursor: pointer;
          pointer-events: auto;
        }
        .tpd-heli-pulse {
          position: absolute; inset: -6px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(255,59,59,0.45) 0%, rgba(255,59,59,0) 70%);
          animation: heliPulse 1.6s ease-in-out infinite;
          pointer-events: none;
        }
        .tpd-heli-body {
          position: absolute; inset: 0;
          display: flex; align-items: center; justify-content: center;
          transition: transform 0.6s ease-out;
          will-change: transform;
        }
        .tpd-heli-rotor {
          position: absolute; inset: -4px;
          border: 1.5px dashed rgba(255,80,80,0.85);
          border-radius: 50%;
          animation: heliRotor 0.4s linear infinite;
        }
        .tpd-heli-icon {
          font-size: 22px;
          line-height: 1;
          filter: drop-shadow(0 0 6px rgba(255,59,59,0.95));
          z-index: 1;
        }
        .tpd-heli-label {
          position: absolute;
          bottom: -16px;
          left: 50%;
          transform: translateX(-50%);
          font-family: monospace;
          font-size: 9px;
          font-weight: bold;
          letter-spacing: 0.1em;
          color: #ff6b6b;
          background: rgba(0,0,0,0.85);
          padding: 1px 5px;
          border: 1px solid rgba(255,59,59,0.5);
          border-radius: 2px;
          white-space: nowrap;
          text-shadow: 0 0 4px rgba(255,59,59,0.6);
        }
        .tpd-heli-marker.tpd-heli-ground .tpd-heli-pulse { animation: none; opacity: 0.35; }
        .tpd-heli-marker.tpd-heli-ground .tpd-heli-rotor { animation-duration: 1.5s; opacity: 0.5; }
        .tpd-heli-marker.tpd-heli-ground .tpd-heli-icon { filter: drop-shadow(0 0 3px rgba(150,150,150,0.7)) grayscale(0.7); }
        .tpd-heli-marker.tpd-heli-ground .tpd-heli-label { color: #888; border-color: #444; }
        @keyframes heliPulse {
          0%,100% { transform: scale(0.85); opacity: 0.55; }
          50%      { transform: scale(1.15); opacity: 0.95; }
        }
        @keyframes heliRotor {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .map-glitch {
          animation: mapGlitch 0.35s steps(1) forwards;
        }
        @keyframes mapGlitch {
          0%   { filter: none; }
          15%  { filter: hue-rotate(20deg) saturate(1.4); }
          30%  { filter: hue-rotate(-10deg) brightness(1.1); }
          60%  { filter: hue-rotate(5deg); }
          100% { filter: none; }
        }
      `}</style>
    </div>
  );
};

export default TacticalMap;
